import Flex from '@Components/Bases/Flex';
import {
  infoCategory,
  useGetInfoCategoryMasterData,
} from '@Domains/MasterData';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useImpactsOnStakeholdersForm } from '../../ImpactsOnStakeholdersFormProvider';
import {
  CategoryMasterDataFormValues,
  StakeholderAnalysisFormValues,
  StakeholderGroupFormValues,
} from '../../ImpactsOnStakeholdersFormProvider/types';
import { sortStakeholderGroups } from '../utils';
import { modalAddStakeholderAnalysisValidationSchema } from '../validators';
import CommunicationActivitiesCheckboxList from './CommunicationActivitiesCheckboxList';
import StakeholderCheckboxList from './StakeholderCheckboxList';
import StakeholderExpectations from './StakeholderExpectations';
import StakeholderExpectationsResponse from './StakeholderExpectationsResponse';
import StakeholderType from './StakeholderType';
import { sortByOthersAtBottom } from './utils';

type ModalAddStakeholderAnalysisProps = {
  open: boolean;
  close: () => void;
  mode: 'create' | 'edit';
  editData?: StakeholderGroupFormValues;
  editIndex?: number;
  rowId?: string;
};

const ModalAddStakeholderAnalysis = ({
  open,
  close,
  mode,
  editData,
  editIndex,
  rowId,
}: ModalAddStakeholderAnalysisProps) => {
  const { getValues, setValue, trigger } = useImpactsOnStakeholdersForm();
  const initialValues = mode === 'edit' ? editData : undefined;

  const [{ data = [] }] = useGetInfoCategoryMasterData([
    infoCategory.STAKEHOLDER,
    infoCategory.COMMUNICATION_ACTIVITY,
  ]);

  const stakeholdersData = data.filter(
    (item) => item.infoCategory === 'STAKEHOLDER'
  );
  const communicationActivitiesData = data.filter(
    (item) => item.infoCategory === 'COMMUNICATION_ACTIVITY'
  );

  const { completedSection } = useWatch({
    name: 'stakeholderAnalysis',
  }) as StakeholderAnalysisFormValues;
  const { isCompletedTh = false, isCompletedEn = false } =
    completedSection ?? {};

  const modalStakeholderContext = useForm({
    defaultValues: initialValues,
    resolver: zodResolver(modalAddStakeholderAnalysisValidationSchema),
    mode: 'onChange',
  });
  const {
    formState: { isValid },
    watch,
    clearErrors,
  } = modalStakeholderContext;

  const stakeholderFields = watch(['stakeholders']);
  const stakeholderTypeFields = watch(['stakeholderType']);
  const communicationActivitiesFields = watch(['communicationActivities']);
  const expectationDescriptionTh = watch('expectationDescriptionTh');
  const expectationDescriptionEn = watch('expectationDescriptionEn');
  const otherStakeholders = watch('otherStakeholders');
  const otherCommunicationActivities = watch('otherCommunicationActivities');

  const hasOtherStakeholders = Boolean(
    stakeholderFields[0]?.some(
      (stakeholder: CategoryMasterDataFormValues) =>
        stakeholder?.nameEn === 'Others'
    )
  );
  const hasOtherCommunicationActivities = Boolean(
    communicationActivitiesFields[0]?.some(
      (activity: CategoryMasterDataFormValues) => activity?.nameEn === 'Others'
    )
  );

  const isSubmitDisabled = useMemo(() => {
    if (!stakeholderTypeFields || !stakeholderFields[0]?.length) return true;
    if (
      mode === 'create' &&
      (!expectationDescriptionTh || !expectationDescriptionEn)
    )
      return true;
    if (hasOtherStakeholders) {
      const isValidOthers = otherStakeholders?.every(
        (stakeholder: CategoryMasterDataFormValues) =>
          Boolean(stakeholder.nameTh && stakeholder.nameEn)
      );
      if (!isValidOthers) return true;
    }
    if (hasOtherCommunicationActivities) {
      const isValidComm = otherCommunicationActivities?.every(
        (activity: CategoryMasterDataFormValues) =>
          Boolean(activity.nameTh && activity.nameEn)
      );
      if (!isValidComm) return true;
    }
    return !isValid;
  }, [
    stakeholderTypeFields,
    expectationDescriptionTh,
    expectationDescriptionEn,
    stakeholderFields,
    otherStakeholders,
    otherCommunicationActivities,
    hasOtherStakeholders,
    hasOtherCommunicationActivities,
    mode,
    isValid,
  ]);

  useEffect(() => {
    if (!hasOtherStakeholders) clearErrors('otherStakeholders');
    if (!hasOtherCommunicationActivities)
      clearErrors('otherCommunicationActivities');
  }, [hasOtherStakeholders, hasOtherCommunicationActivities, clearErrors]);

  const handleSubmitData = useCallback(
    (data: StakeholderGroupFormValues) => {
      let { stakeholders = [], communicationActivities = [] } = data;
      stakeholders = sortByOthersAtBottom(stakeholders);
      communicationActivities = sortByOthersAtBottom(communicationActivities);

      if (!hasOtherStakeholders) {
        data.otherStakeholders = [];
      }
      if (!hasOtherCommunicationActivities) {
        data.otherCommunicationActivities = [];
      }
      data.stakeholders = stakeholders;
      data.communicationActivities = communicationActivities;

      const prevData = getValues('stakeholderAnalysis.stakeholderGroups') || [];

      let updatedData: StakeholderGroupFormValues[];

      if (mode === 'edit' && editIndex !== undefined) {
        updatedData = prevData.map(
          (item: StakeholderGroupFormValues, index: number) => {
            if (rowId?.startsWith('DETAILS_')) {
              return item.id === editIndex ? data : item;
            }
            return index === editIndex - 1 ? data : item;
          }
        );
      } else {
        updatedData = sortStakeholderGroups([...prevData, data]);
      }

      setValue('stakeholderAnalysis.stakeholderGroups', updatedData);

      trigger('stakeholderAnalysis.stakeholderGroups');
      close();
    },
    [
      close,
      editIndex,
      getValues,
      hasOtherCommunicationActivities,
      hasOtherStakeholders,
      mode,
      setValue,
      trigger,
      rowId,
    ]
  );

  const handleClose = useCallback(() => close(), [close]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: '900px',
          padding: '0px 16px',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        },
      }}
    >
      <FormProvider {...modalStakeholderContext}>
        <form>
          <DialogTitle
            sx={{
              padding: '16px 0px',
              fontSize: 16,
              position: 'sticky',
              top: 0,
              zIndex: 99,
              bgcolor: 'white',
              fontWeight: 800,
            }}
          >
            {mode === 'edit'
              ? 'แก้ไขรายละเอียดการวิเคราะห์ผู้มีส่วนได้เสียในห่วงโซ่คุณค่าธุรกิจ'
              : 'เพิ่มรายละเอียดการวิเคราะห์ผู้มีส่วนได้เสียในห่วงโซ่คุณค่าธุรกิจ'}
          </DialogTitle>
          <DialogContent sx={{ padding: '0px' }}>
            <Flex
              gap={2}
              flexDirection={'column'}
              pb={1}
              flex={1}
              overflow={'auto'}
              sx={{ paddingTop: '8px' }}
            >
              <StakeholderCheckboxList
                name="stakeholders"
                value={stakeholdersData}
                isCompletedTh={isCompletedTh}
                isCompletedEn={isCompletedEn}
              />
              <StakeholderType />
              <StakeholderExpectations
                isCompletedTh={isCompletedTh}
                isCompletedEn={isCompletedEn}
              />
              <StakeholderExpectationsResponse
                isCompletedTh={isCompletedTh}
                isCompletedEn={isCompletedEn}
              />
              <CommunicationActivitiesCheckboxList
                name="communicationActivities"
                value={communicationActivitiesData}
                isCompletedTh={isCompletedTh}
                isCompletedEn={isCompletedEn}
              />
            </Flex>
          </DialogContent>
          <DialogActions
            sx={{
              padding: 1,
              position: 'sticky',
              bottom: 0,
              zIndex: 99,
              width: '100%',
              bgcolor: 'white',
              justifyContent: 'flex-end',
            }}
          >
            <Button color="inherit" onClick={handleClose}>
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              onClick={() =>
                modalStakeholderContext.handleSubmit(handleSubmitData)()
              }
              disabled={isSubmitDisabled}
            >
              ยืนยัน
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default ModalAddStakeholderAnalysis;
